import cx from 'classnames';
import { useState } from 'react';

import { Icon } from '@sb/design-system';
import type { IconKind } from '@sb/design-system';

import {
  CHAT_ICON_BUTTON_CLASS,
  CHAT_ICON_BUTTON_STYLE,
} from './chatIconButton';
import type { ChatMenuItemDef } from './ChatMenuItem';
import { ChatPopoverMenu } from './ChatPopoverMenu';
import { ChatWarnings, type ChatWarning } from './ChatWarnings';

/**
 * @deprecated Prefer `ChatMenuItemDef`. Kept as an alias for the
 * existing public prop surface so consumers of `ChatHeader` don't
 * need to update their imports.
 */
export type ChatHeaderMenuItem = ChatMenuItemDef;

export interface ChatHeaderLeftAction {
  iconKind: IconKind;
  ariaLabel: string;
  onClick: () => void;
}

interface ChatHeaderProps {
  title?: string;
  /**
   * Optional kebab menu. When supplied, the header renders the kebab
   * button and a popover that lists these items on click.
   * Click-outside the popover dismisses it. When omitted, the kebab is
   * hidden entirely.
   */
  menuItems?: ChatHeaderMenuItem[];
  /**
   * Optional icon button in the left column (e.g. a back arrow when
   * the panel is showing chat history). Replaces the default empty
   * spacer.
   */
  leftAction?: ChatHeaderLeftAction;
  /**
   * Standing configuration problems with the agent. A non-empty list puts a
   * warning icon left of the kebab, which reveals them on hover; an empty one
   * renders no icon at all.
   */
  warnings?: ChatWarning[];
}

export const DEFAULT_CHAT_TITLE = 'Routine Agent';

/**
 * Header strip at the top of the chat card. Three-column grid (left
 * action / centered title / right warnings + kebab) plus a popover that opens
 * off the kebab when `menuItems` is supplied.
 */
export function ChatHeader({
  title = DEFAULT_CHAT_TITLE,
  menuItems,
  leftAction,
  warnings,
}: ChatHeaderProps) {
  const [isMenuOpen, setIsMenuOpen] = useState(false);

  // Both side columns share a width so the centred title stays optically
  // centred whether or not the warning icon is there: one 32px button, or two
  // with the 4px gap between them.
  const sideColumnWidth = warnings && warnings.length > 0 ? 68 : 32;

  return (
    <div
      className={cx(
        'tw-grid',
        'tw-items-center',
        'tw-pt-12',
        'tw-px-16',
        'tw-pb-10',
        'tw-shrink-0',
        'tw-relative',
      )}
      style={{
        // Custom three-column grid, the specific header drop-shadow,
        // and z-index don't map to design-system tokens, so they stay
        // inline; the surface uses the themeable
        // `--surface-primary-elevated` token.
        gridTemplateColumns: `${sideColumnWidth}px 1fr ${sideColumnWidth}px`,
        background: 'var(--surface-primary-elevated)',
        boxShadow: '0 4px 10px -6px rgba(0,0,0,0.08)',
        zIndex: 2,
      }}
    >
      {/* Left column — back arrow or other icon-button, otherwise an
          empty spacer so the centered title stays optically centered. */}
      <div className="tw-flex tw-justify-start">
        {leftAction ? (
          <button
            type="button"
            onClick={leftAction.onClick}
            aria-label={leftAction.ariaLabel}
            className={CHAT_ICON_BUTTON_CLASS}
            style={CHAT_ICON_BUTTON_STYLE}
          >
            <Icon kind={leftAction.iconKind} className="tw-icon-22" />
          </button>
        ) : null}
      </div>

      {/* Center column — title. */}
      <div className="tw-flex tw-justify-center tw-items-center">
        <span
          className="tw-select-none"
          style={{
            // The design-system typography scale doesn't include 16px
            // font size, 600 weight, or this letter-spacing value, so
            // the title text styling stays inline.
            fontSize: 16,
            fontWeight: 600,
            color: 'var(--label-primary)',
            letterSpacing: -0.3,
          }}
        >
          {title}
        </span>
      </div>

      {/* Right column — warning icon (when there is something to warn
          about) then the kebab + its popover (when menuItems provided). */}
      <div className="tw-flex tw-justify-end tw-items-center tw-gap-4">
        {warnings ? <ChatWarnings warnings={warnings} /> : null}
        {menuItems && menuItems.length > 0 ? (
          <ChatPopoverMenu
            isOpen={isMenuOpen}
            onClose={() => setIsMenuOpen(false)}
            items={menuItems}
            ariaLabel="Chat actions"
            className="tw-flex"
            data-testid="chat-header-menu"
          >
            <button
              type="button"
              onClick={() => setIsMenuOpen((open) => !open)}
              aria-label="Chat menu"
              aria-haspopup="menu"
              aria-expanded={isMenuOpen}
              data-testid="chat-header-menu-button"
              className={CHAT_ICON_BUTTON_CLASS}
              style={CHAT_ICON_BUTTON_STYLE}
            >
              <Icon kind="ellipsisCircle" className="tw-icon-22" />
            </button>
          </ChatPopoverMenu>
        ) : null}
      </div>
    </div>
  );
}
